// src/components/stock/SuppliersSearchPanel.tsx
//
// SuppliersSearchPanel
// - Presentation-only search UI for Suppliers page.
// - Delegates all state ownership to the parent page.
// - Uses the shared SearchPanel container for:
//   - Search / Reset buttons
//   - Optional autoSearch (pass-through only)
//   - Active filter chips (removable, readable)
//
// IMPORTANT (policy):
// - Do NOT hardcode autoSearch defaults here.
//   SearchPanel owns global defaults via constants.
//   This component must pass through `autoSearch` only if provided by the page.

import React, { useMemo } from 'react';
import { Grid } from '@mui/material';

import {
  SearchPanel,
  SearchTextField,
  type ActiveChipsConfig,
  type SearchChipSpec,
  type SetSearchValueFn,
} from '@components/common/search';

import type { GetSuppliersParams } from '@apis/supplier';

type SearchForm = GetSuppliersParams;

export type SuppliersSearchPanelProps = {
  open: boolean;
  onOpenChange: (next: boolean) => void;

  // Draft state (owned by page)
  draftSearch: SearchForm;
  setValue: SetSearchValueFn<SearchForm>;

  // Actions (owned by page)
  onSearch: () => void;
  onReset: () => void;

  // pass-through only
  autoSearch?: { enabled?: boolean; delayMs?: number };
};

const SuppliersSearchPanel: React.FC<SuppliersSearchPanelProps> = ({
  open,
  onOpenChange,
  draftSearch,
  setValue,
  onSearch,
  onReset,
  autoSearch,
}) => {
  /* ------------------------------------------------------------------ */
  /* Active chips                                                       */
  /* ------------------------------------------------------------------ */

  const chips = useMemo<SearchChipSpec<SearchForm>[]>(
    () => [
      {
        key: 'supplierName',
        label: 'Supplier',
        isActive: (s) => Boolean(String(s.supplierName ?? '').trim()),
        getValueText: (s) => String(s.supplierName ?? '').trim(),
        clear: ({ setValue }) => setValue('supplierName', null as any),
      },
      {
        key: 'managerName',
        label: 'Manager',
        isActive: (s) => Boolean(String(s.managerName ?? '').trim()),
        getValueText: (s) => String(s.managerName ?? '').trim(),
        clear: ({ setValue }) => setValue('managerName', null as any),
      },
      {
        key: 'email',
        label: 'E-mail',
        isActive: (s) => Boolean(String(s.email ?? '').trim()),
        getValueText: (s) => String(s.email ?? '').trim(),
        clear: ({ setValue }) => setValue('email', null as any),
      },
      {
        key: 'phoneNumber',
        label: 'Phone',
        isActive: (s) => Boolean(String(s.phoneNumber ?? '').trim()),
        getValueText: (s) => String(s.phoneNumber ?? '').trim(),
        clear: ({ setValue }) => setValue('phoneNumber', null as any),
      },
    ],
    [],
  );

  const chipsConfig = useMemo<ActiveChipsConfig<SearchForm>>(
    () => ({
      enabled: true,
      search: draftSearch,
      setValue,
      chips,
      maxVisible: 2,
      // Keep behavior consistent across pages:
      // chip removal should immediately run a search.
      onAfterClear: onSearch,
    }),
    [chips, draftSearch, onSearch, setValue],
  );

  /* ------------------------------------------------------------------ */
  /* Render                                                             */
  /* ------------------------------------------------------------------ */

  return (
    <SearchPanel
      title="Filters"
      open={open}
      onOpenChange={onOpenChange}
      onSearch={onSearch}
      onReset={onReset}
      chipsConfig={chipsConfig}
      // pass-through only. If undefined, SearchPanel uses global defaults.
      autoSearch={autoSearch}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <SearchTextField
            fullWidth
            label="Supplier"
            placeholder="Enter the supplier name"
            size="small"
            value={draftSearch.supplierName ?? ''}
            onChangeValue={(v) => setValue('supplierName', v)}
            onClear={() => setValue('supplierName', null as any)}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SearchTextField
            fullWidth
            label="Manager"
            placeholder="Enter the manager name"
            size="small"
            value={draftSearch.managerName ?? ''}
            onChangeValue={(v) => setValue('managerName', v)}
            onClear={() => setValue('managerName', null as any)}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SearchTextField
            fullWidth
            label="E-mail"
            placeholder="Enter the e-mail address"
            size="small"
            value={draftSearch.email ?? ''}
            onChangeValue={(v) => setValue('email', v)}
            onClear={() => setValue('email', null as any)}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SearchTextField
            fullWidth
            label="Phone number"
            placeholder="Enter the phone number"
            size="small"
            value={draftSearch.phoneNumber ?? ''}
            onChangeValue={(v) => setValue('phoneNumber', v)}
            onClear={() => setValue('phoneNumber', null as any)}
          />
        </Grid>
      </Grid>
    </SearchPanel>
  );
};

export default SuppliersSearchPanel;
